import type { AdminClient } from "../api.js";
import type {
  AccountPath,
  ChartSnapshot,
  CompanySlug,
  ExpenseAuditItem,
  StaffRole,
} from "../types.js";
import type {
  ActivityBooking,
  ActivityDirection,
  ActivityItem,
  ActivityPage,
  ActivityQuery,
  ActivitySearchQuery,
  ActivitySearchResponse,
  ActivitySources,
  ActivityState,
  BookedBy,
} from "../console/types.js";
import { ConsoleDataError } from "../console/data-source.js";
import { clear, h } from "../dom.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { advice, can } from "../ui/capabilities.js";
import * as face from "../ui/face.js";
import { account as accountFace } from "../ui/face.js";
import { readerRole } from "../ui/facts.js";
import { mountChangeCategory } from "../ui/change-category.js";
import { mountAttachments } from "../ui/attachments.js";
import { auditAccountAllowed } from "../ui/audit-accounts.js";
import {
  disclosure,
  drawer,
  emptyState,
  errorState,
  skeleton,
} from "../ui/components.js";
import {
  activityDescriptionText,
  bankLineStatusWord,
  BOOKED_WORD,
  PROCESSED_ROW_NOTE,
  PROCESSED_WORD,
  realValueText,
  walletKindWord,
} from "../ui/activity-words.js";
import {
  countedText,
  moreText,
  pluralText,
  timeAgoText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import { failureCopy } from "../ui/failure.js";
import { bookingAccountsCapped } from "../ui/hub-intake-words.js";
import {
  NO_MATCHES_ANY_MONTH,
  NO_MATCHES_MONTHS_SEARCHED,
  NOT_EVERY_MONTH_SEARCHED,
  SEARCH_ALL_MONTHS_PROMPT,
  SEARCH_COULD_NOT_FINISH,
  SEARCH_NEWEST_MATCHES,
  SEARCH_OLDER_NOT_SEARCHED,
  SEARCHED_EVERY_MONTH,
} from "../ui/measured-claim.js";
import { formatDecimalMoney } from "../ui/money.js";
import { accountText, slugText } from "../console/report-render.js";
import { csvText, saveCsv } from "../ui/csv-download.js";
import { navigateTo } from "../ui/nav-bus.js";
import { navLabel } from "../ui/sentences.js";
import { setReviewScopeHint } from "../ui/review-scope.js";
import {
  createPeriodControl,
  defaultPeriod,
  formatPeriodLabel,
  shiftPeriod,
} from "../ui/period-control.js";

export interface TransactionsPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  /** The role the shell already knows (decision 10: unknown reads as the AM). */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /** Clock for the default month and for "received for more than a day". */
  now?: Date;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1): the Booked column and the drawer name
   * the accounts a line was booked to, so a between-companies leg names the
   * company and never its code.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

const PAGE_SIZE = 50;
/**
 * The CSV walk's own bound: 50 pages of 100 is 5,000 lines. Past it the file is
 * partial and the person is told, rather than one press reading the history.
 */
const CSV_MAX_PAGES = 50;
/** The Worker caps an All-months search at 500 items; the summary says so. */
const ALL_SEARCH_RESULT_CAP = 500;
const DAY_MS = 24 * 60 * 60 * 1000;

type SourceFilter = "all" | "bank" | "moov";
type StateFilter = "all" | ActivityState;
type StatusRoute = "queue" | "home" | "general-ledger";
type SearchScope = "month" | "all";

/** The five words G7 allows, and nothing inferred when `by` is absent. */
const BOOKED_BY_WORDS: Record<BookedBy, string> = {
  receipt: "From a receipt or bill",
  rule: "By a rule",
  ai: "By AI",
  system_match: "Matched automatically",
  person: "By a person",
};

interface StatusView {
  text: string;
  route?: StatusRoute;
  linkLabel?: string;
}

const SOURCE_FILTERS: ReadonlyArray<{ id: SourceFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "bank", label: "Bank" },
  { id: "moov", label: "Wallet payment" },
];

const STATUS_FILTERS: ReadonlyArray<{ id: StateFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "needs_you", label: "Needs you" },
  { id: "received", label: "Waiting" },
  { id: "approved", label: "Waiting to book" },
  { id: "dismissed", label: "Dismissed" },
  { id: "booked", label: BOOKED_WORD },
  { id: "processed", label: PROCESSED_WORD },
];

/**
 * Activity — bank lines and wallet payments for one month.
 *
 * F1's `dataTable` only accepts plain strings, so it cannot host a source
 * chip, a status link, a fee subline, or a row that opens a drawer. The
 * table below uses the same `table-scroll` / `data-table` classes that
 * helper emits. Amount colour follows the server's money-in-positive sign
 * and is also painted, so direction is not carried by the minus glyph alone.
 */
export async function renderTransactionsPage(opts: TransactionsPageOptions): Promise<void> {
  const { client, company, root } = opts;
  const now = opts.now ?? new Date();
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  clear(root);

  // The reader, from the shell's role (B2b-6): an unknown reader is the account
  // manager (decision 10), never the owner. `data-viewer-role` stays for the
  // mounted tests and the layout pass, and reads the same decision.
  const role: StaffRole = readerRole(opts.role);
  const page = h("div", {
    class: "activity-page",
    "data-role": "activity-page",
    "data-viewer-role": role,
  });
  root.append(page);

  let period = defaultPeriod(now);
  let sourceFilter: SourceFilter = "all";
  let stateFilter: StateFilter = "all";
  // rd15 A4: the server-side filters. A bank account the line came from, a
  // date range across months (both ends or neither), and an amount window.
  let accountFilter = "";
  let fromDate = "";
  let toDate = "";
  let minAmount = "";
  let maxAmount = "";
  /**
   * rd15 A4 fix round 1: the amount window is a magnitude, and "money in or
   * money out" is its own answer. Asking a person to type a minus sign to mean
   * "a charge" is how "Lowest 50" hid every charge; a plain choice is what the
   * Worker and this screen both read now.
   */
  let amountDirection: ActivityDirection = "both";
  /** The accounts the company holds, read once for the filter list. */
  const accountOptions = new Map<string, { name: string; mask: string }>();
  let searchScope: SearchScope = "month";
  /** Committed q for the month view; typing in All months never changes it. */
  let searchText = "";
  /** Committed q for the all-months view, kept separately so a scope switch restores each. */
  let allSearchText = "";
  let allSearchPage = 1;
  let allSearchResult: ActivitySearchResponse | null = null;
  let allSearchSources: ActivitySources = { bank: {}, moov: { walletConfigured: true }, ledger: { available: true } };
  let pageIndex = 1;
  /** The sources of the last list painted, so a file's words match the screen. */
  let lastSources: ActivitySources = {
    bank: {},
    moov: { walletConfigured: true },
    ledger: { available: true },
  };
  let loadToken = 0;
  /** Key of the all-months request now running; the same search is not sent twice. */
  let searchInFlightKey: string | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  page.append(h("h1", { class: "page-title" }, ["Activity"]));
  page.append(
    h("p", { class: "lede" }, [
      "Every bank line and wallet payment for the month is listed here.",
    ]),
  );

  const sourceButtons = new Map<SourceFilter, HTMLButtonElement>();
  const statusButtons = new Map<StateFilter, HTMLButtonElement>();

  const sourceTabs = chipRow(
    "Source",
    "source-filter",
    SOURCE_FILTERS,
    sourceFilter,
    sourceButtons,
    (id) => {
      sourceFilter = id;
      pageIndex = 1;
      paintPressed(sourceButtons, id);
      paintFilterToggle();
      refreshForFilters();
    },
  );
  const statusTabs = chipRow(
    "Status",
    "status-filter",
    STATUS_FILTERS,
    stateFilter,
    statusButtons,
    (id) => {
      stateFilter = id;
      pageIndex = 1;
      paintPressed(statusButtons, id);
      paintFilterToggle();
      refreshForFilters();
    },
  );

  // On a phone the two chip rows collapse behind this one control, so the
  // list itself is what the person sees first. The count is the number of
  // filters currently doing something (source + status).
  const filterToggle = h(
    "button",
    {
      type: "button",
      class: "btn btn-quiet activity-filter-toggle",
      "data-action": "toggle-activity-filters",
      "aria-expanded": "false",
      "aria-controls": "activity-filter-panel",
    },
    ["Filter"],
  );
  const filterPanel = h(
    "div",
    {
      id: "activity-filter-panel",
      class: "activity-filter-panel",
      "data-role": "activity-filters",
    },
    [sourceTabs, statusTabs],
  );
  filterToggle.addEventListener("click", () => {
    const open = filterToggle.getAttribute("aria-expanded") !== "true";
    filterToggle.setAttribute("aria-expanded", open ? "true" : "false");
    toolbar.classList.toggle("filters-open", open);
  });
  function paintFilterToggle(): void {
    const active =
      (sourceFilter === "all" ? 0 : 1) + (stateFilter === "all" ? 0 : 1);
    clear(filterToggle);
    filterToggle.append(
      active === 0 ? "Filter" : `Filter (${String(active)})`,
    );
    filterToggle.setAttribute(
      "aria-label",
      active === 0 ? "Filter" : `Filter, ${String(active)} active`,
    );
  }

  const search = h("input", {
    type: "search",
    class: "field-input activity-search",
    name: "q",
    placeholder: "Search description, property, lease or amount",
    "aria-label": "Search description, property, lease or amount",
    "data-role": "activity-search",
  });
  const scopeControl = searchScopeControl("activity-search-scope", searchScope, (next) => {
    setSearchScope(next);
  });
  const allSearchButton = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary activity-search-all",
      "data-action": "search-all-months",
      "aria-label": "Search all months",
    },
    ["Search"],
  );
  allSearchButton.hidden = true;
  allSearchButton.addEventListener("click", () => {
    runAllSearch();
  });
  search.addEventListener("input", () => {
    if (searchScope === "all") {
      if (searchTimer !== null) {
        clearTimeout(searchTimer);
        searchTimer = null;
      }
      return;
    }
    const value = search.value;
    if (searchTimer !== null) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      searchTimer = null;
      searchText = value;
      pageIndex = 1;
      loadInteractive();
    }, 300);
  });
  search.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" || searchScope !== "all") return;
    event.preventDefault();
    runAllSearch();
  });

  const searchSummary = h("p", {
    class: "activity-search-summary",
    "data-role": "search-summary",
    role: "status",
    "aria-live": "polite",
  });
  searchSummary.hidden = true;

  const periodHost = h("div", { "data-role": "activity-period-host" });
  const presets = h(
    "div",
    { class: "date-presets", "data-role": "date-presets", role: "group", "aria-label": "Month" },
    [],
  );
  const thisMonth = presetButton("this-month", "This month", () => {
    applyPeriod(defaultPeriod(now));
  });
  const lastMonth = presetButton("last-month", "Last month", () => {
    applyPeriod(shiftPeriod(defaultPeriod(now), -1));
  });
  presets.append(thisMonth, lastMonth);

  // --- rd15 A4: bank account, date range and amount, answered by the server ---
  const accountSelect = h("select", {
    class: "field-input",
    name: "account",
    "aria-label": "Filter by bank account",
    "data-role": "activity-account-filter",
  });
  accountSelect.append(h("option", { value: "" }, ["Any bank account"]));
  accountSelect.addEventListener("change", () => {
    accountFilter = accountSelect.value;
    pageIndex = 1;
    refreshForFilters();
  });

  const fromInput = h("input", {
    type: "date",
    class: "field-input",
    name: "from",
    "aria-label": "From date",
    "data-role": "activity-from",
  });
  const toInput = h("input", {
    type: "date",
    class: "field-input",
    name: "to",
    "aria-label": "To date",
    "data-role": "activity-to",
  });
  const rangeNote = h("p", {
    class: "muted",
    "data-role": "activity-range-note",
    role: "status",
  });
  rangeNote.hidden = true;
  /**
   * Apply the two date fields, one end or both (rd16 U, C8).
   *
   * THE DEFECT. One end alone answered "Pick both dates to look across a
   * range, or neither for the month", so a person who typed only From (the
   * QuickBooks habit: "from then on") got nothing. The Worker requires both
   * ends — `ingest-worker/src/activity.ts` refuses half a range with a 400 and
   * `contracts/books-api.yaml` says "from and to (ISO dates, both together)"
   * — so the missing end is filled HERE, in the console, and the note says
   * which window is showing.
   */
  const applyRange = (): void => {
    const answer = activityDateRange({
      from: fromInput.value,
      to: toInput.value,
      today: todayDate(now),
    });
    if (answer.problem !== null) {
      rangeNote.hidden = false;
      clear(rangeNote);
      rangeNote.append(answer.problem);
      return;
    }
    rangeNote.hidden = answer.note === null;
    clear(rangeNote);
    // The note is this page's own sentence (built above from the shared date
    // formatter, never API text); it goes through the one strip like every
    // other string on a face.
    if (answer.note !== null) {
      rangeNote.append(amVisibleDisplayText(answer.note));
    }
    fromDate = answer.from;
    toDate = answer.to;
    pageIndex = 1;
    leaveAllSearchMode();
    mountPeriod();
    setSearchResultsMode(searchScope === "all");
    refreshForFilters();
  };
  fromInput.addEventListener("change", () => {
    applyRange();
    paintClearRange();
  });
  toInput.addEventListener("change", () => {
    applyRange();
    paintClearRange();
  });

  const minInput = h("input", {
    type: "text",
    inputmode: "decimal",
    class: "field-input",
    name: "minAmount",
    placeholder: "Any",
    "aria-label": "Lowest amount",
    "data-role": "activity-min-amount",
  });
  const maxInput = h("input", {
    type: "text",
    inputmode: "decimal",
    class: "field-input",
    name: "maxAmount",
    placeholder: "Any",
    "aria-label": "Highest amount",
    "data-role": "activity-max-amount",
  });
  const applyAmounts = (): void => {
    const min = minInput.value.trim();
    const max = maxInput.value.trim();
    if (!isAmountText(min) || !isAmountText(max)) {
      // rd15 A4 fix round 2 (reviewer L1): the box shows what is actually
      // applied. A value the page will not use is put back to the one the list
      // is filtering on (nothing, when there is none) while the note says what
      // an amount is — otherwise the box reads "-50" beside a list still
      // filtered to 50 and up.
      minInput.value = minAmount;
      maxInput.value = maxAmount;
      rangeNote.hidden = false;
      clear(rangeNote);
      rangeNote.append(
        "An amount is a number like 42.00. Pick money in or money out to say which way.",
      );
      return;
    }
    rangeNote.hidden = true;
    clear(rangeNote);
    minAmount = min;
    maxAmount = max;
    pageIndex = 1;
    refreshForFilters();
  };
  minInput.addEventListener("change", () => {
    applyAmounts();
  });
  maxInput.addEventListener("change", () => {
    applyAmounts();
  });

  const directionSelect = h("select", {
    class: "field-input",
    name: "direction",
    "aria-label": "Money in or money out",
    "data-role": "activity-amount-direction",
  });
  for (const [value, label] of [
    ["both", "Both"],
    ["in", "Money in"],
    ["out", "Money out"],
  ] as const) {
    directionSelect.append(h("option", { value }, [label]));
  }
  directionSelect.value = amountDirection;
  directionSelect.addEventListener("change", () => {
    amountDirection =
      directionSelect.value === "in" || directionSelect.value === "out"
        ? directionSelect.value
        : "both";
    pageIndex = 1;
    refreshForFilters();
  });

  const clearRange = h(
    "button",
    { type: "button", class: "btn btn-quiet", "data-action": "clear-activity-range" },
    ["Clear dates"],
  );
  clearRange.hidden = true;
  clearRange.addEventListener("click", () => {
    fromInput.value = "";
    toInput.value = "";
    fromDate = "";
    toDate = "";
    pageIndex = 1;
    mountPeriod();
    setSearchResultsMode(searchScope === "all");
    paintClearRange();
    refreshForFilters();
  });

  /**
   * "Clear dates" shows whenever either field holds a date (rd16 U, C8). It
   * used to appear only when BOTH ends were applied, so one typed date left no
   * visible way to undo it.
   */
  function paintClearRange(): void {
    clearRange.hidden = fromInput.value === "" && toInput.value === "";
  }

  const extraFilters = h("div", { class: "activity-extra-filters" }, [
    filterField("Bank account", accountSelect),
    filterField("From", fromInput),
    filterField("To", toInput),
    clearRange,
    filterField("Lowest amount", minInput),
    filterField("Highest amount", maxInput),
    filterField("Money in or out", directionSelect),
  ]);
  filterPanel.append(extraFilters, rangeNote);

  const downloadButton = h(
    "button",
    {
      type: "button",
      class: "btn btn-quiet activity-download",
      "data-action": "download-activity-csv",
    },
    ["Download CSV"],
  );
  const downloadStatus = h("p", {
    class: "muted",
    "data-role": "activity-download-status",
    role: "status",
    "aria-live": "polite",
  });
  downloadStatus.hidden = true;
  let downloadInFlight = false;
  downloadButton.addEventListener("click", () => {
    if (downloadInFlight) return;
    void downloadCsv();
  });

  const toolbar = h("div", { class: "toolbar activity-toolbar" }, [
    search,
    scopeControl,
    allSearchButton,
    filterToggle,
    filterPanel,
    presets,
    periodHost,
    downloadButton,
    downloadStatus,
  ]);
  page.append(toolbar);
  paintFilterToggle();
  page.append(searchSummary);

  const count = h("p", { class: "activity-count", "data-role": "transactions-count" }, []);
  count.hidden = true;
  page.append(count);

  const tableWrap = h("div", { "data-role": "transactions-table-wrap" });
  page.append(tableWrap);

  const pager = h("div", { class: "pager", "data-role": "transactions-pager" });
  page.append(pager);

  function applyPeriod(next: string): void {
    // Leaving All months always reloads the month, even when the month is
    // unchanged: the search table on screen is not the month's table.
    const left = leaveAllSearchMode();
    if (next === period && !left) return;
    period = next;
    pageIndex = 1;
    mountPeriod();
    loadInteractive();
  }

  function mountPeriod(): void {
    clear(periodHost);
    periodHost.append(
      createPeriodControl({
        period,
        ariaLabel: "Period",
        onChange: (next) => {
          leaveAllSearchMode();
          period = next;
          pageIndex = 1;
          loadInteractive();
        },
      }),
    );
  }

  function hideCount(): void {
    clear(count);
    count.hidden = true;
  }

  function showCount(total: number): void {
    clear(count);
    if (total <= 0) {
      count.hidden = true;
      return;
    }
    count.hidden = false;
    count.append(`${String(total)} transactions`);
  }

  /**
   * The Booked filter spans two server states, so the server total is not the
   * number of booked lines. Name the scope of the number instead of showing a
   * wrong total: what is on this page, and that more may follow.
   */
  function showBookedCount(activity: ActivityPage): void {
    clear(count);
    const lines = pluralText(activity.items.length, "booked line", "booked lines");
    if (lines === "") {
      count.hidden = true;
      return;
    }
    count.hidden = false;
    count.append(
      activity.hasMore
        ? `${lines} on this page; more pages to check.`
        : `${lines} on this page.`,
    );
  }

  /** The same scope rule for the Processed filter's page. */
  function showProcessedCount(activity: ActivityPage): void {
    clear(count);
    const lines = pluralText(activity.items.length, "processed line", "processed lines");
    if (lines === "") {
      count.hidden = true;
      return;
    }
    count.hidden = false;
    count.append(
      activity.hasMore
        ? `${lines} on this page; more pages to check.`
        : `${lines} on this page.`,
    );
  }

  mountPeriod();
  // The account filter lists the accounts the company holds, read once. A
  // failed read is not an error the page must show: the filter simply offers
  // no accounts, and every line still shows its own.
  void fillAccountOptions();
  await loadInner();

  async function fillAccountOptions(): Promise<void> {
    try {
      const balances = await client.getBalances(company);
      for (const account of balances.accounts) {
        if (account.accountId === undefined) continue;
        if (accountOptions.has(account.accountId)) continue;
        accountOptions.set(account.accountId, {
          name: amVisibleDisplayText(account.name),
          mask: amVisibleDisplayText(account.mask),
        });
      }
      const selected = accountSelect.value;
      clear(accountSelect);
      accountSelect.append(h("option", { value: "" }, ["Any bank account"]));
      for (const [id, account] of accountOptions) {
        accountSelect.append(
          h("option", { value: id }, [`${account.name} \u00b7 ${account.mask}`]),
        );
      }
      accountSelect.value = selected;
    } catch {
      // The list is a convenience; the server still filters by an account id.
    }
  }

  function loadInteractive(): void {
    void loadInner();
  }

  function refreshForFilters(): void {
    if (searchScope === "all") {
      if (allSearchText.trim() === "") {
        showSearchPrompt();
        return;
      }
      runAllSearch(allSearchText);
      return;
    }
    loadInteractive();
  }

  function setSearchScope(next: SearchScope): void {
    if (next === searchScope) return;
    searchScope = next;
    paintSearchScope(scopeControl, next);
    if (next === "all") {
      clearMonthFilters();
      search.value = allSearchText;
      allSearchButton.hidden = false;
      hideSearchSummary();
      // The month presets and period picker do not apply to an all-months
      // search, so they are out of the way as soon as that scope is chosen.
      setSearchResultsMode(true);
      return;
    }
    search.value = searchText;
    allSearchButton.hidden = true;
    hideSearchSummary();
    setSearchResultsMode(false);
    // The month load below takes its own token; this one also drops any
    // all-months reply still on its way.
    invalidateSearch();
    pageIndex = 1;
    loadInteractive();
  }

  /** Returns true when All months was showing and has now been left. */
  function leaveAllSearchMode(): boolean {
    if (searchScope !== "all") return false;
    searchScope = "month";
    paintSearchScope(scopeControl, "month");
    search.value = searchText;
    allSearchButton.hidden = true;
    hideSearchSummary();
    setSearchResultsMode(false);
    invalidateSearch();
    return true;
  }

  /** A search reply that arrives after this point must never paint. */
  function invalidateSearch(): void {
    loadToken += 1;
    searchInFlightKey = null;
    allSearchResult = null;
  }

  /**
   * While All months results are showing, the month controls are hidden, as on
   * Books. A date range takes their place for the same reason: the month is
   * not what is being read.
   */
  function setSearchResultsMode(active: boolean): void {
    const hide = active || rangeActive();
    presets.hidden = hide;
    periodHost.hidden = hide;
    paintClearRange();
    // rd15 A4 fix round 1: a search of every month takes the words, the source
    // and the status, and nothing else. The month's own filters and the file
    // would describe a different view, so they are out of the way rather than
    // quietly ignored: the file must always be what is on screen.
    extraFilters.hidden = active;
    downloadButton.hidden = active;
    if (active) downloadStatus.hidden = true;
  }

  /** Leaving the month's own view also leaves its filters behind. */
  function clearMonthFilters(): void {
    accountFilter = "";
    accountSelect.value = "";
    minAmount = "";
    maxAmount = "";
    minInput.value = "";
    maxInput.value = "";
    amountDirection = "both";
    directionSelect.value = "both";
    fromDate = "";
    toDate = "";
    fromInput.value = "";
    toInput.value = "";
    rangeNote.hidden = true;
    clear(rangeNote);
  }

  function rangeActive(): boolean {
    return fromDate !== "" && toDate !== "";
  }

  function hideSearchSummary(): void {
    clear(searchSummary);
    searchSummary.hidden = true;
  }

  function showSearchSummary(text: string): void {
    clear(searchSummary);
    searchSummary.hidden = false;
    searchSummary.append(text);
  }

  function showSearchPrompt(): void {
    clear(tableWrap);
    clear(pager);
    hideCount();
    closeDrawer();
    setSearchResultsMode(true);
    invalidateSearch();
    showSearchSummary(SEARCH_ALL_MONTHS_PROMPT);
  }

  function runAllSearch(committed?: string): void {
    const raw = committed ?? search.value;
    const q = raw.trim();
    if (q === "") {
      allSearchText = raw;
      showSearchPrompt();
      return;
    }
    allSearchText = raw;
    const query: ActivitySearchQuery = { q };
    if (sourceFilter !== "all") query.source = sourceFilter;
    // Every Booked row is not one server state: a booked wallet payment is
    // state=booked, a booked bank line is state=processed with a booking. The
    // request below asks for both and keeps the rows whose word is Booked.
    if (stateFilter !== "all" && stateFilter !== "booked" && stateFilter !== "processed") {
      query.state = stateFilter;
    }
    // Enter twice, or Enter then Search, is one search: the same request is
    // not sent again while it is still running. A different search is sent;
    // the token below makes sure only the newest one paints.
    const key = JSON.stringify([
      query,
      stateFilter === "booked"
        ? "booked+processed"
        : stateFilter === "processed"
          ? "processed"
          : "one-state",
    ]);
    if (searchInFlightKey === key) return;
    searchInFlightKey = key;
    const token = ++loadToken;
    closeDrawer();
    hideCount();
    clear(pager);
    clear(tableWrap);
    setSearchResultsMode(true);
    tableWrap.append(skeleton(4));
    showSearchSummary("Searching every month…");
    void runAllSearchRequest(query, token);
  }

  /**
   * One Booked answer for all months, built the same way as the month view:
   * ask for both server states and keep the rows whose word is Booked. The
   * match count is exact whenever neither search was capped; when either was,
   * the summary says results are capped rather than showing a wrong number.
   */
  async function searchAllMonths(
    query: ActivitySearchQuery,
  ): Promise<ActivitySearchResponse> {
    if (stateFilter === "processed") {
      const processed = await client.searchActivity(company, {
        ...query,
        state: "processed",
      });
      const items = sortNewestFirst(
        processed.items.filter(
          (row) => bankLineStatusWord(row) === PROCESSED_WORD,
        ),
      );
      return { ...processed, items, matchCount: items.length };
    }
    if (stateFilter !== "booked") return client.searchActivity(company, query);
    const [booked, processed] = await Promise.all([
      client.searchActivity(company, { ...query, state: "booked" }),
      client.searchActivity(company, { ...query, state: "processed" }),
    ]);
    return mergeBookedSearch(booked, processed);
  }

  async function runAllSearchRequest(
    query: ActivitySearchQuery,
    token: number,
  ): Promise<void> {
    try {
      const result = await searchAllMonths(query);
      if (token !== loadToken || !stillCurrent()) return;
      searchInFlightKey = null;
      allSearchResult = result;
      allSearchSources = searchSources();
      allSearchPage = 1;
      paintAllSearch(result);
    } catch (error) {
      if (token !== loadToken || !stillCurrent()) return;
      searchInFlightKey = null;
      allSearchResult = null;
      clear(tableWrap);
      clear(pager);
      hideCount();
      // The sentence is shown once, in the results area, with Try again.
      hideSearchSummary();
      tableWrap.append(
        searchFailure(activitySearchErrorSentence(error, role), () => {
          runAllSearch(allSearchText);
        }),
      );
    }
  }

  function paintAllSearch(result: ActivitySearchResponse): void {
    lastSources = allSearchSources;
    clear(tableWrap);
    clear(pager);
    hideCount();
    paintSearchSummary(result);
    if (result.items.length === 0) {
      // A normal-sized sentence in the results area, not a small grey line.
      tableWrap.append(
        emptyState(noMatchSentence(result, sourceFilter !== "all" || stateFilter !== "all")),
      );
      return;
    }
    const start = (allSearchPage - 1) * PAGE_SIZE;
    const items = result.items.slice(start, start + PAGE_SIZE);
    tableWrap.append(renderRows(items, allSearchSources, true));
    renderSearchPager(result.items.length, !result.capped);
  }

  function renderSearchPager(total: number, complete: boolean): void {
    clear(pager);
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const prev = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "search-prev",
        "aria-label": "Previous results page",
      },
      ["‹ Prev"],
    );
    const next = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "search-next",
        "aria-label": "Next results page",
      },
      ["Next ›"],
    );
    if (allSearchPage <= 1) prev.disabled = true;
    if (allSearchPage >= totalPages) next.disabled = true;
    prev.addEventListener("click", () => {
      if (allSearchPage <= 1) return;
      allSearchPage -= 1;
      if (allSearchResult !== null) paintAllSearch(allSearchResult);
    });
    next.addEventListener("click", () => {
      if (allSearchPage >= totalPages) return;
      allSearchPage += 1;
      if (allSearchResult !== null) paintAllSearch(allSearchResult);
    });
    pager.append(
      prev,
      h("span", { class: "pager-info", "data-role": "search-page-info" }, [
        `Page ${String(allSearchPage)} of ${String(totalPages)} \u00b7 ${matchCountText(total, complete)}`,
      ]),
      next,
    );
  }

  function paintSearchSummary(result: ActivitySearchResponse): void {
    const parts: string[] = [];
    const from = result.searchedFrom === undefined ? null : shortSearchMonth(result.searchedFrom);
    const to = result.searchedTo === undefined ? null : shortSearchMonth(result.searchedTo);
    // Activity's allMonthsSearched is false only when the Worker's own bounds
    // (months, rows, bytes, 500 results) left older months out. Months whose
    // books view is missing were still searched, so they are a separate line.
    if (from !== null && to !== null) {
      if (result.allMonthsSearched) {
        parts.push(`Searched every month (${from} to ${to}).`);
      } else {
        parts.push(`Searched ${from} to ${to}.`);
        parts.push(SEARCH_OLDER_NOT_SEARCHED);
      }
    } else {
      parts.push(result.allMonthsSearched ? SEARCHED_EVERY_MONTH : NOT_EVERY_MONTH_SEARCHED);
    }
    if (result.capped) {
      parts.push(SEARCH_NEWEST_MATCHES);
    } else if (result.items.length > 0) {
      parts.push(`${matchCountText(result.matchCount, !result.capped)}.`);
    }
    if (result.monthsWithoutBooksView > 0) {
      const n = result.monthsWithoutBooksView;
      parts.push(
        `Booked status could not be checked for ${String(n)} ${n === 1 ? "month" : "months"}.`,
      );
    }
    showSearchSummary(parts.join(" "));
  }

  function searchSources(): ActivitySources {
    // The all-months response does not carry per-month sources. Status words
    // here stay factual: a received row is still waiting; no month claim is made.
    return { bank: {}, moov: { walletConfigured: true }, ledger: { available: true } };
  }

  async function loadInner(): Promise<void> {
    if (searchScope !== "month") return;
    const token = ++loadToken;
    hideSearchSummary();
    hideCount();
    clear(pager);
    closeDrawer();
    clear(tableWrap);
    tableWrap.append(
      h("p", { class: "muted", "data-role": "activity-loading", role: "status" }, ["Loading..."]),
      skeleton(4),
    );
    try {
      const activity = await listMonth();
      if (token !== loadToken || !stillCurrent()) return;
      paint(activity);
    } catch (error) {
      if (token !== loadToken || !stillCurrent()) return;
      hideCount();
      clear(pager);
      clear(tableWrap);
      const state = errorState(activityErrorSentence(error, role), () => {
        loadInteractive();
      });
      const retry = state.querySelector("button");
      if (retry !== null) {
        retry.textContent = "Try again";
        retry.setAttribute("aria-label", "Try again");
      }
      tableWrap.append(state);
    }
  }

  function buildQuery(pageSize = PAGE_SIZE, page = pageIndex): ActivityQuery {
    // The month (or the range) comes first: the request-budget harness prints
    // this object and pins the month's own shape.
    const query: ActivityQuery = {};
    // A date range spans months, so the month is not named; the server reads
    // the months the range covers and totals the filtered view.
    if (fromDate !== "" && toDate !== "") {
      query.from = fromDate;
      query.to = toDate;
    } else {
      query.period = period;
    }
    query.page = page;
    query.pageSize = pageSize;
    const q = searchText.trim();
    if (q !== "") query.q = q;
    if (sourceFilter !== "all") query.source = sourceFilter;
    if (stateFilter !== "all") query.state = stateFilter;
    if (accountFilter !== "") query.account = accountFilter;
    if (minAmount !== "") query.minAmount = minAmount;
    if (maxAmount !== "") query.maxAmount = maxAmount;
    if (amountDirection !== "both") query.direction = amountDirection;
    return query;
  }

  /**
   * Exactly what the filters show, page by page, at the server's own page
   * size. The walk stops at a bound so one press cannot read the whole
   * history; when it stops early the person is told the file is partial.
   */
  async function collectForCsv(): Promise<{ items: ActivityItem[]; complete: boolean }> {
    const csvPageSize = 100;
    const items: ActivityItem[] = [];
    for (let page = 1; page <= CSV_MAX_PAGES; page += 1) {
      const answer = await listForQuery(buildQuery(csvPageSize, page));
      items.push(...answer.items);
      if (!answer.hasMore) return { items, complete: true };
    }
    return { items, complete: false };
  }

  /**
   * One page for an arbitrary query. The Booked and Processed filters span two
   * server states, so they are built the same way the screen builds them — the
   * file must hold what the screen shows, not a different set.
   */
  async function listForQuery(query: ActivityQuery): Promise<ActivityPage> {
    if (stateFilter !== "booked" && stateFilter !== "processed") {
      return client.listActivity(company, query);
    }
    const base: ActivityQuery = { ...query };
    delete base.state;
    if (stateFilter === "processed") {
      const processed = await client.listActivity(company, {
        ...base,
        state: "processed",
        pageSize: query.pageSize ?? PAGE_SIZE,
        page: query.page ?? 1,
      });
      const items = processed.items.filter(
        (row) => bankLineStatusWord(row) === PROCESSED_WORD,
      );
      return { ...processed, items, total: items.length };
    }
    const half = Math.max(1, Math.floor((query.pageSize ?? PAGE_SIZE) / 2));
    const [bookedPage, processedPage] = await Promise.all([
      client.listActivity(company, { ...base, state: "booked", pageSize: half }),
      client.listActivity(company, { ...base, state: "processed", pageSize: half }),
    ]);
    const kept = new Map<string, ActivityItem>();
    for (const row of [...bookedPage.items, ...processedPage.items]) {
      if (bankLineStatusWord(row) !== BOOKED_WORD) continue;
      if (!kept.has(row.id)) kept.set(row.id, row);
    }
    const items = sortNewestFirst([...kept.values()]);
    return {
      ...bookedPage,
      items,
      total: items.length,
      hasMore: bookedPage.hasMore || processedPage.hasMore,
    };
  }

  async function downloadCsv(): Promise<void> {
    // The file is the month view's own lines. A search of every month has no
    // file, because it has no filters to describe (rd15 A4 fix round 1).
    if (searchScope === "all") return;
    downloadInFlight = true;
    downloadButton.disabled = true;
    downloadStatus.hidden = false;
    clear(downloadStatus);
    downloadStatus.append("Collecting the lines…");
    try {
      const collected = await collectForCsv();
      if (!stillCurrent()) return;
      const rows = collected.items.map((item) => [
        item.date.slice(0, 7),
        item.date,
        // The file is data, not the face: the full description the row holds,
        // never the capped "… and N more" display line (rd16 U review, item 5).
        amVisibleDisplayText(item.description),
        item.source === "moov" ? "Wallet payment" : "Bank",
        accountWords(item),
        statusView(item, sourcesForCsv(), now, false).text,
        bookingCellText(item, companies),
        item.amount,
      ]);
      const csv = csvText(
        ["Month", "Date", "Description", "Source", "Bank account", "Status", "Booked to", "Amount"],
        rows,
      );
      saveCsv(activityCsvName(company, fromDate, toDate, period), csv);
      clear(downloadStatus);
      downloadStatus.append(
        collected.complete
          ? `Downloaded ${pluralText(collected.items.length, "line", "lines")}.`
          : `The file holds the first ${String(collected.items.length)} lines. Narrow the filters to export the rest.`,
      );
    } catch (error) {
      if (!stillCurrent()) return;
      clear(downloadStatus);
      downloadStatus.append(activityErrorSentence(error, role));
    } finally {
      downloadInFlight = false;
      downloadButton.disabled = false;
    }
  }

  /** The sources a file's status words may rely on: the month's own read. */
  function sourcesForCsv(): ActivitySources {
    return lastSources;
  }

  /**
   * The month list, newest first. The portal sorts even though the Worker's
   * /activity route already orders the same way: the page must not depend on
   * an ordering guarantee no contract states (Q6 records the measurement).
   *
   * The Booked filter spans two server states (`booked` wallet rows and
   * `processed` bank rows that carry a booking), so it asks for both and
   * keeps the rows whose word is Booked. The response has no way to ask for
   * that union in one request; the count for this filter names only what is
   * on screen rather than inventing a total.
   */
  async function listMonth(): Promise<ActivityPage> {
    const query = buildQuery();
    if (stateFilter !== "booked" && stateFilter !== "processed") {
      return client.listActivity(company, query);
    }
    const base: ActivityQuery = { ...query };
    delete base.state;
    // Half a page per state, so the merged page is still one PAGE_SIZE long.
    // Paging each state independently covers every row exactly once as the
    // person turns the pages, without asking for all rows in one request.
    const halfPage = Math.max(1, Math.floor(PAGE_SIZE / 2));
    if (stateFilter === "processed") {
      const processedPage = await client.listActivity(company, {
        ...base,
        state: "processed",
        pageSize: PAGE_SIZE,
      });
      const items = sortNewestFirst(
        processedPage.items.filter(
          (row) => bankLineStatusWord(row) === PROCESSED_WORD,
        ),
      );
      return {
        ...processedPage,
        items,
        total: items.length,
        hasMore: processedPage.hasMore,
      };
    }
    const [bookedPage, processedPage] = await Promise.all([
      client.listActivity(company, { ...base, state: "booked", pageSize: halfPage }),
      client.listActivity(company, { ...base, state: "processed", pageSize: halfPage }),
    ]);
    const kept = new Map<string, ActivityItem>();
    for (const row of [...bookedPage.items, ...processedPage.items]) {
      if (bankLineStatusWord(row) !== BOOKED_WORD) continue;
      if (!kept.has(row.id)) kept.set(row.id, row);
    }
    const items = sortNewestFirst([...kept.values()]);
    return {
      ...bookedPage,
      items,
      total: items.length,
      hasMore: bookedPage.hasMore || processedPage.hasMore,
    };
  }

  function paint(activity: ActivityPage): void {
    lastSources = activity.sources;
    clear(tableWrap);
    clear(pager);
    if (activity.items.length === 0) {
      hideCount();
      if (stateFilter === "booked" && activity.hasMore) {
        // The server has more pages, but this page's rows carried no booking
        // (their word is Processed). Say so and keep Next reachable; never
        // dead-end on "Nothing matches this search."
        tableWrap.append(emptyState("No booked lines on this page."));
        renderPager(activity);
        return;
      }
      if (stateFilter === "processed" && activity.hasMore) {
        // The server has more pages, but this page's rows all carried a
        // booking (their word is Booked), so say what this page holds — not
        // "No processed lines", which reads as a claim about the month when
        // more rows are behind this page (rd L2 review round 1).
        tableWrap.append(emptyState("None on this page."));
        renderPager(activity);
        return;
      }
      if (activity.hasMore) {
        // An empty page with more behind it is not an empty month (rd Z4 9a):
        // "Nothing in <month> yet" would be a claim this page cannot make.
        tableWrap.append(emptyState("Nothing on this page."));
        renderPager(activity);
        return;
      }
      const filtered = filtersActive();
      // A filter that finds nothing is not a failed load, and neither is an
      // empty month: only a failed read offers Try again (rd Z4 C-9).
      // The month is not the wallet's fault: a company may have a wallet and
      // no bank, or a bank and nothing this month.
      const block = emptyState(
        emptyActivityMonthCopy(period, activity.sources, filtered, now),
      );
      const noBank = !filtered && !bankLinesRead(activity.sources);
      if (noBank) appendNoBankLine(block, role);
      tableWrap.append(block);
      return;
    }
    if (stateFilter === "booked") {
      showBookedCount(activity);
    } else if (stateFilter === "processed") {
      showProcessedCount(activity);
    } else {
      showCount(activity.total);
    }
    tableWrap.append(renderRows(sortNewestFirst(activity.items), activity.sources, false));
    renderPager(activity);
  }

  function filtersActive(): boolean {
    return (
      searchText.trim() !== "" ||
      sourceFilter !== "all" ||
      stateFilter !== "all" ||
      rangeActive() ||
      accountFilter !== "" ||
      minAmount !== "" ||
      maxAmount !== ""
    );
  }

  function renderRows(
    items: readonly ActivityItem[],
    sources: ActivitySources,
    withMonth: boolean,
  ): HTMLElement {
    const head = h("tr", {}, [
      withMonth ? h("th", { scope: "col" }, ["Month"]) : null,
      h("th", { scope: "col" }, ["Date"]),
      h("th", { scope: "col" }, ["Description"]),
      h("th", { scope: "col" }, ["Source"]),
      h("th", { scope: "col" }, ["Status"]),
      h("th", { scope: "col" }, ["Booked to"]),
      h("th", { scope: "col", class: "num" }, ["Amount"]),
    ]);
    const body = h("tbody", {});
    for (const item of items) body.append(renderRow(item, sources, withMonth));
    const table = h(
      "table",
      {
        class: withMonth ? "data-table activity-table activity-table--search" : "data-table activity-table",
        "aria-label": withMonth ? "Activity search results" : "Activity",
      },
      [h("thead", {}, [head]), body],
    );
    return h("div", { class: "table-scroll", "data-role": "data-table" }, [table]);
  }

  /**
   * `withMonth` is true only for All months search hits. A hit carries no
   * per-month sources, so its status never makes a books-view claim.
   */
  function renderRow(
    item: ActivityItem,
    sources: ActivitySources,
    withMonth = false,
  ): HTMLTableRowElement {
    const plain = plainWords(item);
    const subline = moovSubline(item);
    const moneyOut = item.amount.startsWith("-");
    const monthLabel = withMonth ? shortSearchMonth(item.date.slice(0, 7)) : null;
    const row = h("tr", {
      class: "activity-row",
      "data-activity-id": item.id,
      "data-source": item.source,
      "data-state": item.state,
      tabindex: "0",
    });
    // Every row used to be named just "Open Bank line"; a person moving by row
    // could not tell them apart. Date, description and amount say which one.
    const descriptionLine = descriptionText(item);
    const rowName = `${plain}, ${formatActivityDate(item.date)}, ${descriptionLine}, ${formatAmount(item.amount)}`;
    row.setAttribute("aria-label", `Open ${rowName}`);
    const description = h("td", { class: "activity-cell-desc" }, [
      h("span", { class: "activity-desc-text" }, [descriptionLine]),
    ]);
    const accountLine = accountWords(item);
    if (accountLine !== "") {
      description.append(
        h("span", { class: "activity-subline", "data-role": "activity-account" }, [
          accountLine,
        ]),
      );
    }
    if (subline !== null) {
      description.append(h("span", { class: "activity-subline" }, [subline]));
    }
    description.append(
      h("span", { class: "activity-mobile-meta" }, [
        mobileMeta(item, sources, now, monthLabel, withMonth, companies),
      ]),
    );
    if (monthLabel !== null) {
      row.append(
        h("td", { class: "activity-cell-month", "data-role": "search-result-month" }, [monthLabel]),
      );
    }
    row.append(
      h("td", { class: "activity-cell-date" }, [formatActivityDate(item.date)]),
      description,
      h("td", { class: "activity-cell-source" }, [sourceChip(item.source)]),
      h("td", { class: "activity-cell-status" }, [statusControl(item, sources, withMonth)]),
      bookedToCell(item, companies),
      h(
        "td",
        {
          class: moneyOut ? "num activity-cell-amount activity-out" : "num activity-cell-amount activity-in",
          "data-role": "txn-amount",
          "data-direction": moneyOut ? "out" : "in",
        },
        [formatAmount(item.amount)],
      ),
    );
    row.addEventListener("click", (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest("button, a")) return;
      openDrawer(item, sources, withMonth);
    });
    row.addEventListener("keydown", (event) => {
      if (event.target !== row) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openDrawer(item, sources, withMonth);
    });
    return row;
  }

  function statusControl(
    item: ActivityItem,
    sources: ActivitySources,
    searchHit: boolean,
  ): HTMLElement {
    const view = statusView(item, sources, now, searchHit);
    const chipClass = "chip activity-status";
    if (view.route === undefined) {
      const cell = h("span", { class: "activity-status-cell" }, [
        h(
          "span",
          { class: chipClass, "data-role": "activity-status", "data-state": item.state },
          [view.text],
        ),
      ]);
      // A processed line with no booking of its own carries a short plain
      // explanation beside the word, so "Processed" never stands alone with
      // nothing said about where the money went (rd L2 review round 1).
      if (item.state === "processed" && item.booking === undefined) {
        cell.append(
          h(
            "span",
            { class: "activity-subline", "data-role": "activity-status-note" },
            [PROCESSED_ROW_NOTE],
          ),
        );
      }
      return cell;
    }
    const wrap = h("span", {
      class: "activity-status-wrap",
      "data-role": "activity-status",
      "data-state": item.state,
    });
    wrap.append(h("span", { class: chipClass }, [view.text]));
    if (view.linkLabel !== undefined) {
      const link = h(
        "button",
        {
          type: "button",
          class: "btn btn-quiet",
          "data-action": statusAction(view.route),
        },
        [view.linkLabel],
      );
      link.addEventListener("click", () => {
        go(view.route, item);
      });
      wrap.append(link);
    } else {
      const button = h(
        "button",
        {
          type: "button",
          class: `${chipClass} activity-status-link`,
          "data-action": statusAction(view.route),
          "data-role": "activity-status",
          "data-state": item.state,
        },
        [view.text],
      );
      button.addEventListener("click", () => {
        go(view.route, item);
      });
      return button;
    }
    return wrap;
  }

  function go(route: StatusRoute | undefined, item: ActivityItem): void {
    if (route === "general-ledger") {
      navigateTo("general-ledger", { period: item.date.slice(0, 7) });
      return;
    }
    if (route === "queue") {
      // A "Needs you" row names one payment. Hand To review its company and
      // month, plus the payment's own reference, so the card that was counted
      // opens instead of the unfiltered fleet (round 10, carried LOW from
      // round 8). A card that is not in the list falls back to that scope.
      setReviewScopeHint({
        company,
        month: item.date.slice(0, 7),
        openCardRef: { source: item.source, refId: item.refId },
      });
      navigateTo(route);
      return;
    }
    if (route === "home") navigateTo(route);
  }

  /**
   * rd15 lane A wiring of lane B's "Change category" control (owner decisions
   * 5-7). The drawer names one line of one booking, so the control is written
   * against the audit item the Worker holds for that line: a change carries the
   * booking's hash, so it can never be written against words on screen.
   *
   * WHAT THIS PAGE DOES NOT DECIDE. Whether a line may be re-categorised is the
   * pipeline's eligibility (income and expenses yes; transfers, loans, equity
   * lines and cash accounts no), which is why a line that is not in the audit
   * read leaves the slot empty. Who may change it is `capabilities.ts`, and the
   * closed-month sentence is the module's own. The chart read supplies what may
   * be picked from, filtered by the same rule the Expense audit page uses.
   */
  let chartSnapshot: ChartSnapshot | null = null;
  let chartInFlight: Promise<ChartSnapshot | null> | null = null;

  /** The company's chart, read at most once per visit to this page. */
  async function chartForControl(): Promise<ChartSnapshot | null> {
    if (chartSnapshot !== null) return chartSnapshot;
    chartInFlight ??= client.getChartSnapshot(company).then(
      (snapshot) => {
        chartSnapshot = snapshot;
        return snapshot;
      },
      () => null,
    );
    return chartInFlight;
  }

  /** The audit item for exactly this line, or null when it is not one. */
  async function auditItemForLine(item: ActivityItem): Promise<ExpenseAuditItem | null> {
    const kind = item.source === "moov" ? "moov" : "plaid";
    try {
      // The line's own day, so the answer is a handful rather than a month's
      // queue. `status: all`, because an already-reviewed line is still one a
      // person may change.
      const read = await client.listExpenseAuditItems(company, {
        from: item.date,
        to: item.date,
        status: "all",
      });
      return (
        read.items.find((row) => row.targetKind === kind && row.targetId === item.refId) ?? null
      );
    } catch {
      // The control is an addition to this drawer, never the drawer: a read
      // that fails leaves the slot empty instead of turning a line's detail
      // into an error page.
      return null;
    }
  }

  /**
   * Fill the change-category slot for `item`. Nothing is painted until every
   * read has arrived, and nothing is painted at all for a line the control does
   * not support (no booking, a split, a generated line, a role the capability
   * table does not give it to).
   */
  async function fillChangeCategorySlot(item: ActivityItem, slot: HTMLElement): Promise<void> {
    clear(slot);
    if (!can(role, "decide_expense_audit")) return;
    if (item.booking === undefined) return;
    const [audit, chart] = await Promise.all([auditItemForLine(item), chartForControl()]);
    if (audit === null || chart === null || !slot.isConnected) return;
    const decision = audit.latestDecision;
    const pending =
      decision !== null && decision.status === "pending_apply"
        ? {
            decisionId: decision.id,
            legs: decision.newLegs.length > 0 ? decision.newLegs : audit.currentLegs,
          }
        : null;
    // ONE reader of what is booked: with a change waiting for the run, the
    // lines it will apply are the ones the person is shown.
    const legs = (pending?.legs ?? audit.currentLegs).map((leg) => ({
      account: leg.account,
      location: leg.location,
      amount: leg.amount,
    }));
    // One line, and a category the control can offer. A split needs Split, and
    // a generated line (a transfer, a loan, an equity line) has no category a
    // person chose.
    if (legs.length !== 1) return;
    const booked = legs[0];
    // The audit rule takes the wire text; the branded path stops here.
    if (booked === undefined || !auditAccountAllowed(accountText(booked.account))) return;
    const announced = h("p", {
      class: "muted",
      role: "status",
      "aria-live": "polite",
      "data-role": "change-category-announced",
    });
    const handle = mountChangeCategory({
      role,
      item: {
        company: company,
        targetId: audit.targetId,
        legsSha256: audit.legsSha256,
        periodLocked: audit.periodLocked,
        legs,
        documentIds: audit.documents.map((document) => document.documentId),
        pending,
      },
      accounts: chart.selectableAccounts.filter((account) => auditAccountAllowed(account)),
      locations:
        chart.selectableLocations.length > 0 ? chart.selectableLocations : chart.locations,
      labelCompanies: companies,
      client,
      reload: async () => {
        await fillChangeCategorySlot(item, slot);
      },
      announce: (message) => {
        announced.textContent = message;
      },
    });
    slot.append(handle.root, announced);
  }

  function openDrawer(item: ActivityItem, sources: ActivitySources, searchHit = false): void {
    closeDrawer();
    const body = h("div", { class: "activity-drawer-body" });
    const plain = plainWords(item);
    // The drawer title already says the row's plain name; do not repeat it.
    const descriptionLine = descriptionText(item);
    if (descriptionLine !== plain) {
      body.append(h("p", {}, [descriptionLine]));
    }
    body.append(
      fact("Date", formatActivityDate(item.date)),
      fact("Amount", formatAmount(item.amount)),
    );
    if (item.fee !== undefined && item.fee !== "") body.append(fact("Fee", formatAmount(item.fee)));
    if (item.net !== undefined && item.net !== "") body.append(fact("Net", formatAmount(item.net)));
    body.append(fact("Source", item.source === "moov" ? "Wallet payment" : "Bank"));
    const accountLine = accountWords(item);
    if (accountLine !== "") body.append(fact("Bank account", accountLine));
    const view = statusView(item, sources, now, searchHit);
    body.append(fact("Status", view.text));
    if (item.state === "processed" && item.booking === undefined) {
      // A processed line with no proven booking is the pipeline's terminal
      // mark, not a booking claim: the wallet pass may have consumed the line
      // (its money is in the books through the wallet entry) or the line was
      // removed and never booked. Say it neutrally and never imply the money is
      // missing (rd L2 review round 1). When the month's books copy is not
      // loaded, "no entry of its own" is not a fact this page has.
      body.append(
        h(
          "p",
          { class: "muted", "data-role": "activity-processed-not-booked" },
          [
            sources.ledger.available
              ? "Handled by the nightly run. This line has no entry of its own in the books."
              : "The books for this month are not loaded, so we cannot show a booking.",
          ],
        ),
      );
    }
    if (view.route !== undefined && !canOpenInBooks(item)) {
      const label =
        view.route === "queue"
          ? `Go to ${navLabel("queue")}`
          : (view.linkLabel ?? navLabel("queue"));
      const follow = h(
        "button",
        { type: "button", class: "btn btn-quiet", "data-action": statusAction(view.route) },
        [label],
      );
      follow.addEventListener("click", () => {
        go(view.route, item);
      });
      body.append(follow);
    }
    const bookedPeriod = periodOfItem(item) ?? period;
    // No booking means "we cannot say" (a corrected line, an ambiguous match,
    // a month whose books view is unavailable), never "nothing was booked".
    // The drawer makes no booked-in-books claim of its own without one.
    if (item.booking !== undefined) {
      body.append(bookingDetails(item.booking, bookedPeriod, companies));
    }
    const bookingLocation = item.booking?.location;
    const bookingShowsProperty = bookingLocation !== undefined && bookingLocation !== "";
    // rd16 U, C7: the sandbox's wallet rows carried a property and a lease of
    // nothing but x characters. The console cannot fix that data, but filler
    // must not be shown as if it were a real address or a lease.
    const walletProperty = moovPropertyText(item);
    if (!bookingShowsProperty && walletProperty !== "") {
      body.append(fact("Property", walletProperty));
    }
    const walletLease = moovLeaseText(item);
    if (walletLease !== "") {
      body.append(fact("Lease", walletLease));
    }
    if (item.booking !== undefined) {
      if (sources.ledger.available) {
        const see = h(
          "button",
          { type: "button", class: "btn btn-primary", "data-action": "see-in-books" },
          ["See in Books"],
        );
        see.addEventListener("click", () => {
          // Open the month the booking names. The bank reference id is never
          // typed into a visible search box.
          navigateTo("general-ledger", { period: bookedPeriod });
        });
        body.append(see);
      } else {
        // There is a claimed booking but the loaded books copy for the month
        // is not on screen, so there is nothing to open. Say so once instead
        // of leaving a button that cannot do anything.
        body.append(
          h("p", {
            class: "muted activity-booked-not-found",
            role: "status",
            "data-role": "booked-not-found",
          }, [`Not found in the books for ${formatPeriodLabel(bookedPeriod)}.`]),
        );
      }
    }
    const technical = h("div", {});
    technical.append(fact("Reference", amVisibleDisplayText(item.refId)));
    if (item.id !== item.refId) {
      technical.append(fact("Line reference", amVisibleDisplayText(item.id)));
    }
    if (item.booking !== undefined && item.booking.accounts.length > 0) {
      technical.append(
        fact(
          "Booked accounts",
          item.booking.accounts
            .map((account) => amVisibleDisplayText(accountFace(account, companies)))
            .join(" · "),
        ),
      );
    }
    if (item.moov?.kind !== undefined && item.moov.kind !== "") {
      technical.append(fact("Kind", walletKindWord(item.moov.kind)));
    }
    body.append(disclosure("Show details", technical));
    // rd15 lanes B and C mount here: "Change category" (invariant: it writes a
    // new entry, never an edit) and "Attach a file" (GRANT G15/G15b).
    const changeSlot = h("div", { class: "activity-slot", "data-slot": "change-category" });
    const attachSlot = h("div", { class: "activity-slot", "data-slot": "attachments" });
    body.append(changeSlot, attachSlot);
    page.append(drawer(plain, body, closeDrawer));
    void fillChangeCategorySlot(item, changeSlot);
    void fillAttachmentsSlot(item, attachSlot);
  }

  /**
   * The signed-in reader, for the attachment control's own-file rule. The page
   * is not given the identity by the shell, so `/me` is the one source (the
   * shell already reads it once for the role; this is cached per render). A
   * failed read falls back to the page's role and an empty identity, which
   * fails closed: the account manager is offered no file they cannot withdraw,
   * and the owner's row is still the server's to allow.
   */
  let viewerPromise: Promise<{ role: StaffRole; identity: string }> | null = null;
  function readViewer(): Promise<{ role: StaffRole; identity: string }> {
    if (viewerPromise === null) {
      viewerPromise = client.getMe().then(
        (me) => ({ role: me.role, identity: me.identity }),
        () => ({ role, identity: "" }),
      );
    }
    return viewerPromise;
  }

  /** The attachment slot: one file at a time, the same control everywhere. */
  async function fillAttachmentsSlot(item: ActivityItem, slot: HTMLElement): Promise<void> {
    clear(slot);
    const viewer = await readViewer();
    if (!slot.isConnected) return;
    // rd16 U, C11: "Attached." and "Withdrawn." used to appear twice — once in
    // the control's own status line (which is already a live region) and once
    // in this host's announced mirror. The control owns the sentence; the host
    // adds none.
    const handle = mountAttachments({
      role: viewer.role,
      company,
      source: item.source,
      refId: item.refId,
      viewerIdentity: viewer.identity,
      client,
    });
    slot.append(handle.root);
  }

  function closeDrawer(): void {
    page.querySelector('[data-role="drawer"]')?.remove();
  }

  function renderPager(activity: ActivityPage): void {
    clear(pager);
    const hasNext = transactionsPageHasNext(activity);
    const prev = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "transactions-prev",
        "aria-label": "Previous page",
      },
      ["‹ Prev"],
    );
    const next = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "transactions-next",
        "aria-label": "Next page",
      },
      ["Next ›"],
    );
    if (pageIndex <= 1) prev.disabled = true;
    if (!hasNext) next.disabled = true;
    prev.addEventListener("click", () => {
      if (pageIndex <= 1) return;
      pageIndex -= 1;
      loadInteractive();
    });
    next.addEventListener("click", () => {
      if (!transactionsPageHasNext(activity)) return;
      pageIndex += 1;
      loadInteractive();
    });
    pager.append(prev, next);
  }
}

function chipRow<T extends string>(
  label: string,
  role: string,
  options: ReadonlyArray<{ id: T; label: string }>,
  selected: T,
  buttons: Map<T, HTMLButtonElement>,
  onChange: (id: T) => void,
): HTMLElement {
  const group = h("div", {
    class: "report-tabs",
    role: "tablist",
    "aria-label": label,
    "data-role": role,
  });
  for (const option of options) {
    const pressed = option.id === selected;
    const button = h(
      "button",
      {
        type: "button",
        class: pressed ? "nav-btn active" : "nav-btn",
        role: "tab",
        "aria-selected": pressed ? "true" : "false",
        [`data-${role}`]: option.id,
      },
      [option.label],
    );
    button.addEventListener("click", () => {
      onChange(option.id);
    });
    buttons.set(option.id, button);
    group.append(button);
  }
  return group;
}

function paintPressed<T extends string>(buttons: Map<T, HTMLButtonElement>, active: T): void {
  for (const [id, button] of buttons) {
    const pressed = id === active;
    button.classList.toggle("active", pressed);
    button.setAttribute("aria-selected", pressed ? "true" : "false");
  }
}

function presetButton(id: string, label: string, onClick: () => void): HTMLButtonElement {
  const button = h(
    "button",
    { type: "button", class: "btn btn-quiet", "data-preset": id },
    [label],
  );
  button.addEventListener("click", () => {
    onClick();
  });
  return button;
}

function sourceChip(source: ActivityItem["source"]): HTMLElement {
  const label = source === "moov" ? "Wallet payment" : "Bank";
  const tone = source === "moov" ? "chip chip-info" : "chip";
  return h("span", { class: tone, "data-role": "source-chip" }, [label]);
}

function plainWords(item: ActivityItem): string {
  if (item.source === "bank") return "Bank line";
  if (item.moov?.kind !== undefined && item.moov.kind !== "") {
    return walletKindWord(item.moov.kind);
  }
  return "Wallet payment";
}

/**
 * The row's description, in words (rd16 U, C7). A wallet payment's description
 * is a list the Worker built, so it is de-duplicated and capped; a bank line's
 * is a merchant name and is left as the bank sent it.
 */
function descriptionText(item: ActivityItem): string {
  return amVisibleDisplayText(
    activityDescriptionText(item.description, { wallet: item.source === "moov" }),
  );
}

/** The wallet's property and lease, only when they are real values (rd16 U, C7). */
function moovPropertyText(item: ActivityItem): string {
  return amVisibleDisplayText(realValueText(item.moov?.property));
}

function moovLeaseText(item: ActivityItem): string {
  return amVisibleDisplayText(realValueText(item.moov?.lease));
}

function moovSubline(item: ActivityItem): string | null {
  if (item.source !== "moov") return null;
  const parts: string[] = [];
  if (item.fee !== undefined && item.fee !== "") parts.push(`Fee ${formatAmount(item.fee)}`);
  if (item.net !== undefined && item.net !== "") parts.push(`Net ${formatAmount(item.net)}`);
  const lease = moovLeaseText(item);
  if (lease !== "") parts.push(`Lease ${lease}`);
  const property = moovPropertyText(item);
  if (property !== "") parts.push(property);
  if (parts.length === 0) return null;
  return parts.join(" · ");
}

function mobileMeta(
  item: ActivityItem,
  sources: ActivitySources,
  now: Date,
  monthLabel: string | null,
  searchHit: boolean,
  companies: AccountLabelContext,
): string {
  const parts = [
    monthLabel,
    formatActivityDate(item.date),
    item.source === "moov" ? "Wallet payment" : "Bank",
    statusView(item, sources, now, searchHit).text,
  ].filter((part): part is string => part !== null && part !== "");
  const account = accountWords(item);
  if (account !== "") parts.push(account);
  const booking = bookingCellText(item, companies);
  if (booking !== "") parts.push(booking);
  return parts.join(" · ");
}

function bookedToCell(item: ActivityItem, companies: AccountLabelContext): HTMLElement {
  const cell = h("td", {
    class: "activity-cell-booked",
    "data-role": "booked-to",
  });
  const booking = item.booking;
  if (booking === undefined) return cell;
  const accounts = visibleBookingAccounts(booking, companies);
  if (accounts.length === 0) return cell;
  const first = accounts[0];
  if (first === undefined) return cell;
  cell.setAttribute("data-account", first.raw);
  const label = h(
    "span",
    { class: "activity-booked-label", "data-account": first.raw },
    [first.label],
  );
  cell.append(label);
  if (accounts.length > 1) {
    cell.append(
      ` + ${moreText(accounts.length - 1, !bookingAccountsCapped(booking.accounts.length))}`,
    );
  }
  const by = booking.by === undefined ? "" : BOOKED_BY_WORDS[booking.by];
  if (by !== "") cell.append(h("span", { class: "activity-booked-by" }, [by]));
  return cell;
}

function bookingCellText(item: ActivityItem, companies: AccountLabelContext): string {
  if (item.booking === undefined) return "";
  const accounts = visibleBookingAccounts(item.booking, companies);
  const first = accounts[0];
  if (first === undefined) return "";
  const more =
    accounts.length > 1
      ? ` + ${moreText(accounts.length - 1, !bookingAccountsCapped(item.booking.accounts.length))}`
      : "";
  const by = item.booking.by === undefined ? "" : BOOKED_BY_WORDS[item.booking.by];
  return by === "" ? `${first.label}${more}` : `${first.label}${more} · ${by}`;
}

function visibleBookingAccounts(
  booking: ActivityBooking,
  companies: AccountLabelContext,
): Array<{ raw: AccountPath; label: string }> {
  const out: Array<{ raw: AccountPath; label: string }> = [];
  const seen = new Set<AccountPath>();
  for (const account of booking.accounts) {
    // The one account face (B2b-7): `accountDisplayName` is a helper B5 retires,
    // and the label comes from `ui/face.ts` like every other account word.
    const raw: AccountPath = account;
    if (raw === "" || seen.has(raw)) continue;
    seen.add(raw);
    out.push({ raw, label: amVisibleDisplayText(accountFace(raw, companies)) });
  }
  return out;
}

/**
 * How many months one Activity range may span.
 *
 * LOCKSTEP with the Worker (`ingest-worker/src/activity.ts` `MAX_RANGE_MONTHS`
 * and `contracts/books-api.yaml`: "A range longer than 24 months is 400"). A
 * one-ended "up to this date" has no start, so the console starts it at the
 * first day of the oldest month this route can read, and says so.
 */
export const ACTIVITY_MAX_RANGE_MONTHS = 24;

/** A resolved range, or the plain sentence refusing what was typed. */
export interface ActivityRangeAnswer {
  /** The two ends the request will carry (both empty = the month view). */
  from: string;
  to: string;
  /** What the range is, when one end was filled in by this page. */
  note: string | null;
  /** Why the typed dates cannot be used, or null. */
  problem: string | null;
}

/** `YYYY-MM-DD` for a clock's own calendar day (local, like the page's dates). */
export function todayDate(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** The first day of the month `months` before `iso`'s own month. */
function firstOfMonthBack(iso: string, months: number): string {
  const match = /^(\d{4})-(\d{2})/.exec(iso);
  if (match === null) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return "";
  const shifted = new Date(Date.UTC(year, month - 1 - months, 1));
  const nextMonth = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  return `${String(shifted.getUTCFullYear())}-${nextMonth}-01`;
}

/**
 * How many month prefixes a range touches, inclusive — the same number the
 * Worker builds before it refuses (LOCKSTEP with `rangeMonths` /
 * `MAX_RANGE_MONTHS` in `ingest-worker/src/activity.ts`).
 */
export function monthsSpanned(from: string, to: string): number {
  const startMatch = /^(\d{4})-(\d{2})/.exec(from);
  const endMatch = /^(\d{4})-(\d{2})/.exec(to);
  if (startMatch === null || endMatch === null) return 0;
  const start = Number(startMatch[1]) * 12 + (Number(startMatch[2]) - 1);
  const end = Number(endMatch[1]) * 12 + (Number(endMatch[2]) - 1);
  return end - start + 1;
}

/**
 * The date range a person asked for, from one end or both (rd16 U, C8).
 *
 * QuickBooks reads From alone as "from then on" and To alone as "up to then".
 * The Worker cannot: both ends are required, and half a range is a 400. So the
 * console completes the missing end, and the note names the window it sends
 * rather than letting the person believe it is the whole history:
 *
 *   * From alone -> that day to today.
 *   * To alone -> the first day of the oldest month this route reads, to that
 *     day, and the note prints that start date.
 *
 * TWO REFUSALS, BOTH BEFORE THE REQUEST.
 *
 *   * The Worker reads at most {@link ACTIVITY_MAX_RANGE_MONTHS} month prefixes
 *     and answers a longer range with a 400, which the page could only show as
 *     "Activity could not load. Try again." forever. A longer range is refused
 *     here, in words that name the limit. It is NOT silently clamped: a totals
 *     view that quietly reviews two years while the fields say five would be a
 *     wrong number, not a shortcut (rd16 U review, item 2).
 *   * A first date after the second cannot be read at all.
 *
 * A date after today is deliberately ALLOWED (rd16 U review, item 3): a
 * current-month range often ends after today (1st to the 30th), the bank's own
 * dates are not this browser's clock, and a future day simply has no rows.
 */
export function activityDateRange(input: {
  from: string;
  to: string;
  today: string;
}): ActivityRangeAnswer {
  const from = input.from.trim();
  const to = input.to.trim();
  if (from === "" && to === "") return { from: "", to: "", note: null, problem: null };
  if (from !== "" && to !== "" && from > to) {
    return {
      from: "",
      to: "",
      note: null,
      problem: `The first date is after the second. Pick ${formatActivityDate(to)} or earlier.`,
    };
  }
  if (from !== "" && to === "") {
    if (from > input.today) {
      return {
        from: "",
        to: "",
        note: null,
        problem: `The first date is after today. Pick ${formatActivityDate(input.today)} or earlier.`,
      };
    }
    if (monthsSpanned(from, input.today) > ACTIVITY_MAX_RANGE_MONTHS) {
      return { from: "", to: "", note: null, problem: tooLongRangeProblem() };
    }
    return {
      from,
      to: input.today,
      note: `Showing everything from ${formatActivityDate(from)} up to today.`,
      problem: null,
    };
  }
  if (from === "" && to !== "") {
    const floor = firstOfMonthBack(to, ACTIVITY_MAX_RANGE_MONTHS - 1);
    const start = floor === "" ? to : floor;
    return {
      from: start,
      to,
      note: `Showing everything from ${formatActivityDate(start)} up to ${formatActivityDate(to)}.`,
      problem: null,
    };
  }
  if (monthsSpanned(from, to) > ACTIVITY_MAX_RANGE_MONTHS) {
    return { from: "", to: "", note: null, problem: tooLongRangeProblem() };
  }
  return { from, to, note: null, problem: null };
}

/**
 * The one sentence for a range the Worker would refuse. It names the limit and
 * the next step; it never says a number the page cannot keep.
 */
function tooLongRangeProblem(): string {
  return `That range is longer than ${String(ACTIVITY_MAX_RANGE_MONTHS)} months. This page reads up to ${String(ACTIVITY_MAX_RANGE_MONTHS)} months at a time, so pick a shorter range.`;
}

/** Newest first; equal dates keep the order the server sent (stable sort). */
function sortNewestFirst(items: readonly ActivityItem[]): ActivityItem[] {
  return [...items].sort((left, right) =>
    left.date === right.date ? 0 : left.date < right.date ? 1 : -1,
  );
}

/** Merge the two state searches behind the one Booked filter. */
function mergeBookedSearch(
  booked: ActivitySearchResponse,
  processed: ActivitySearchResponse,
): ActivitySearchResponse {
  const kept = new Map<string, ActivityItem>();
  for (const row of booked.items) {
    if (bankLineStatusWord(row) === BOOKED_WORD) kept.set(row.id, row);
  }
  for (const row of processed.items) {
    if (bankLineStatusWord(row) !== BOOKED_WORD) continue;
    if (!kept.has(row.id)) kept.set(row.id, row);
  }
  const merged = sortNewestFirst([...kept.values()]);
  const froms = [booked.searchedFrom, processed.searchedFrom].filter(
    (value): value is string => value !== undefined,
  );
  const tos = [booked.searchedTo, processed.searchedTo].filter(
    (value): value is string => value !== undefined,
  );
  const searchedFrom = froms.sort()[0];
  const sortedTos = tos.sort();
  const searchedTo = sortedTos[sortedTos.length - 1];
  const capped = booked.capped || processed.capped || merged.length > ALL_SEARCH_RESULT_CAP;
  return {
    items: merged.slice(0, ALL_SEARCH_RESULT_CAP),
    // Count the rows this filter can actually show, not the Worker's per-state
    // match total: a state=booked wallet row can come back without a booking
    // (an ambiguous join), so its word is Processed and it is dropped here.
    // When a search was capped the summary shows the capped sentence instead.
    matchCount: merged.length,
    capped,
    ...(searchedFrom !== undefined ? { searchedFrom } : {}),
    ...(searchedTo !== undefined ? { searchedTo } : {}),
    allMonthsSearched: booked.allMonthsSearched && processed.allMonthsSearched,
    monthsWithoutBooksView: Math.max(
      booked.monthsWithoutBooksView,
      processed.monthsWithoutBooksView,
    ),
  };
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** "Oct 7, 2026" — never an ISO date on the face. */
export function formatActivityDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return amVisibleDisplayText(iso);
  const year = match[1];
  const month = SHORT_MONTHS[Number(match[2]) - 1];
  const day = Number(match[3]);
  if (year === undefined || month === undefined || day < 1) {
    return amVisibleDisplayText(iso);
  }
  return `${month} ${String(day)}, ${year}`;
}

function fact(label: string, value: string): HTMLElement {
  return h("p", { class: "activity-fact" }, [
    h("span", { class: "activity-fact-label" }, [`${label}: `]),
    value,
  ]);
}

/** A one-click way from a booking's source to the page that names it. */
function sourceButton(
  action: string,
  label: string,
  onClick: () => void,
): HTMLElement {
  const button = h(
    "button",
    { type: "button", class: "btn btn-quiet", "data-action": action },
    [label],
  );
  button.addEventListener("click", () => {
    onClick();
  });
  return button;
}

function bookingDetails(
  booking: ActivityBooking,
  period: string,
  companies: AccountLabelContext,
): HTMLElement {
  const block = h("div", { class: "activity-booking", "data-role": "booking-details" });
  block.append(h("h3", {}, ["Booked to"]));
  const list = h("ul", { class: "activity-booking-list" });
  const accounts = visibleBookingAccounts(booking, companies);
  for (const account of accounts) {
    list.append(h("li", { "data-account": account.raw }, [account.label]));
  }
  if (accounts.length === 0) list.append(h("li", {}, ["No account is shown."]));
  block.append(list);
  if (booking.by !== undefined) {
    // rd15 A2: which rule, or which person, in plain words. A rule is named by
    // its number and opens on the Rules page; a person is named by their login
    // because there is no display-name field (`face.person` refuses anything
    // that is not shaped like one).
    const ruleId = booking.ruleId;
    if (booking.by === "rule" && ruleId !== undefined) {
      block.append(fact("How", `Rule ${String(ruleId)}`));
      block.append(
        sourceButton("open-rule", `Open rule ${String(ruleId)}`, () => {
          navigateTo("rules", { rule: ruleId });
        }),
      );
    } else if (booking.by === "person") {
      block.append(fact("How", BOOKED_BY_WORDS.person));
      const who = face.person(booking.person);
      if (who !== null) block.append(fact("Who", who));
    } else {
      block.append(fact("How", BOOKED_BY_WORDS[booking.by]));
    }
    if (booking.by === "receipt") {
      block.append(
        sourceButton(
          "open-receipt-shelf",
          `Open ${navLabel("receipt-shelf")}`,
          () => {
            navigateTo("receipt-shelf");
          },
        ),
      );
    } else if (booking.by === "rule" && booking.ruleId === undefined) {
      block.append(
        sourceButton("open-rules", `Open ${navLabel("rules")}`, () => {
          navigateTo("rules");
        }),
      );
    }
  }
  if (booking.location !== undefined && booking.location !== "") {
    block.append(fact("Property", amVisibleDisplayText(booking.location)));
  }
  block.append(
    h("p", { class: "activity-booked-when", "data-role": "booked-when" }, [
      `As booked in ${formatPeriodLabel(period)}.`,
    ]),
  );
  return block;
}

function periodOfItem(item: ActivityItem): string | null {
  const match = /^(\d{4}-(?:0[1-9]|1[0-2]))/.exec(item.date);
  return match?.[1] ?? null;
}

const SHORT_SEARCH_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** `2026-10` -> `Oct 2026`; anything unexpected is shown as safe text. */
function shortSearchMonth(period: string): string {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  if (match === null) return amVisibleDisplayText(period);
  const year = match[1];
  const month = SHORT_SEARCH_MONTHS[Number(match[2]) - 1];
  if (year === undefined || month === undefined) return amVisibleDisplayText(period);
  return `${month} ${year}`;
}

function searchScopeControl(
  name: string,
  selected: SearchScope,
  onChange: (next: SearchScope) => void,
): HTMLElement {
  const group = h("div", {
    class: "search-scope",
    role: "radiogroup",
    "aria-label": "Search range",
    "data-role": "search-scope",
  });
  for (const option of [
    { id: "month" as const, label: "This month only" },
    { id: "all" as const, label: "All months" },
  ]) {
    const input = h("input", {
      type: "radio",
      name,
      value: option.id,
      "data-scope": option.id,
    });
    input.checked = option.id === selected;
    input.addEventListener("change", () => {
      if (input.checked) onChange(option.id);
    });
    group.append(h("label", { class: "search-scope-option" }, [input, option.label]));
  }
  return group;
}

function paintSearchScope(group: HTMLElement, selected: SearchScope): void {
  for (const input of group.querySelectorAll<HTMLInputElement>('input[type="radio"]')) {
    input.checked = input.value === selected;
  }
}

/** "1 match", "2 matches". */
function matchCountText(count: number, complete: boolean): string {
  return countedText(count, complete, "match", "matches");
}

/**
 * The zero-result sentence. "any month" is only said when every month was
 * searched and nothing was left unchecked; otherwise it names what was covered
 * (the range is in the summary line above).
 */
function noMatchSentence(result: ActivitySearchResponse, filterOn: boolean): string {
  const sentence =
    result.allMonthsSearched && result.monthsWithoutBooksView === 0
      ? NO_MATCHES_ANY_MONTH
      : NO_MATCHES_MONTHS_SEARCHED;
  // A source or status chip narrows the search too, so say so: otherwise
  // "any month" reads as "nowhere", with a chip the person forgot they pressed.
  return filterOn ? `${sentence} A filter is on, so clear it to look wider.` : sentence;
}

/** One failure sentence, once, with Try again. No page-load heading: the page loaded. */
function searchFailure(sentence: string, onTry: () => void): HTMLElement {
  const retry = tryAgainButton(onTry);
  retry.className = "btn btn-primary";
  return h("div", { class: "empty-state", role: "alert", "data-role": "search-error" }, [
    h("p", {}, [sentence]),
    retry,
  ]);
}

function activitySearchErrorSentence(error: unknown, role: StaffRole): string {
  const status = error instanceof ConsoleDataError ? error.status : 0;
  if (status === 401 || status === 403 || status === 429) {
    return failureCopy(error, { role, context: "read" }).sentence;
  }
  return SEARCH_COULD_NOT_FINISH;
}

function tryAgainButton(onTry: () => void): HTMLButtonElement {
  const button = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "retry", "aria-label": "Try again" },
    ["Try again"],
  );
  button.addEventListener("click", () => {
    onTry();
  });
  return button;
}

function canOpenInBooks(item: ActivityItem): boolean {
  return item.booking !== undefined;
}

function statusAction(route: StatusRoute): string {
  if (route === "queue") return "open-review";
  if (route === "home") return "open-home";
  return "open-books";
}

function statusView(
  item: ActivityItem,
  sources: ActivitySources,
  now: Date,
  searchHit = false,
): StatusView {
  if (item.state === "needs_you") {
    return { text: bankLineStatusWord(item), route: "queue" };
  }
  // A card the manager sent to the owner: To review does not show it to them
  // (the Worker hides escalated rows from the manager), so there is no link to
  // press. Naming it is the whole answer (rd Z8).
  if (item.state === "with_the_owner") {
    return { text: bankLineStatusWord(item) };
  }
  if (item.state === "approved") return { text: bankLineStatusWord(item) };
  if (item.state === "dismissed") return { text: bankLineStatusWord(item) };
  // Booked only when the loaded books copy proves the booking; a bank line
  // without one says Processed (the Home word).
  if (item.state === "booked" || item.state === "processed") {
    return { text: bankLineStatusWord(item) };
  }
  // A search hit comes with no books-view facts for its month, so it says
  // only what the row itself says. No "Booking has not picked this up yet".
  if (item.source === "moov" && !searchHit) return receivedMoov(item, sources, now);
  return { text: bankLineStatusWord(item) };
}

function receivedMoov(item: ActivityItem, sources: ActivitySources, now: Date): StatusView {
  if (!sources.ledger.available) {
    return {
      text: "Waiting. The books view has not loaded this month yet.",
      route: "general-ledger",
      linkLabel: "Books",
    };
  }
  if (receivedLongerThanADay(item.date, now)) {
    return {
      text: "Waiting. Booking has not picked this up yet.",
      route: "home",
      linkLabel: "Home",
    };
  }
  return { text: "Waiting" };
}

function receivedLongerThanADay(date: string, now: Date): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const start = new Date(year, month - 1, day).getTime();
  return now.getTime() - start > DAY_MS;
}

/**
 * The empty-month sentence (rd Z4 C-9). The wallet's connection used to be
 * blamed for an empty month ("Moov wallet is not connected for this company"),
 * with a Try again that could not help. An empty month says so, in the month's
 * own words; whether a bank is connected is a separate fact, said separately
 * and only when this page's data supports it.
 */
function emptyActivityMonthCopy(
  period: string,
  sources: ActivitySources,
  filtered: boolean,
  now: Date,
): string {
  if (filtered) return "Nothing matches this search.";
  const month = formatPeriodLabel(period);
  const line = `Nothing in ${month} yet.`;
  // The read time is the wallet's, and it is said in plain words: never "Moov"
  // (rd-polish-rules §1) and never a raw timestamp.
  const lastReadAt = sources.moov.lastReadAt;
  if (lastReadAt === undefined) return line;
  const when = timeAgoText(lastReadAt, now);
  if (when === "") return line;
  return `${line} The wallet was last read ${when}.`;
}

/** True when this page's data shows a bank line has been read for the company. */
function bankLinesRead(sources: ActivitySources): boolean {
  return sources.bank.lastSyncedAt !== undefined;
}

/**
 * What the page can prove about the bank when no bank line has been read
 * (round-2 MUST-FIX 4). `sources.bank` is `{}` both for a company with no bank
 * and for a bank that was just linked and has never synced — a new item has no
 * `last_synced_at` (ingest-worker/src/plaid.ts) and the Activity read adds the
 * field only when the column has a value (ingest-worker/src/activity.ts). So
 * the page never says "no bank connected yet": it says what the read supports
 * and, for the owner, where to look.
 */
function appendNoBankLine(block: HTMLElement, role: StaffRole): void {
  if (role === "account_manager") {
    // round-11 item 10: Check now is the account manager's, so the AM is told to
    // press it — never to ask the owner to do what the AM can do.
    block.append(
      h("p", { class: "muted", "data-role": "activity-no-bank" }, [
        `No bank lines have been read yet. ${advice("check_now", role)}`,
      ]),
    );
    return;
  }
  const link = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "open-bank-accounts",
      "aria-label": navLabel("balances"),
    },
    [`Check the connection in ${navLabel("balances")}.`],
  );
  link.addEventListener("click", () => {
    navigateTo("balances");
  });
  block.append(
    h("p", { class: "muted", "data-role": "activity-no-bank" }, [
      "No bank lines have been read yet. ",
      link,
    ]),
  );
}

function activityErrorSentence(error: unknown, role: StaffRole): string {
  const status = error instanceof ConsoleDataError ? error.status : 0;
  if (status === 401 || status === 403 || status === 429) {
    return failureCopy(error, { role, context: "read" }).sentence;
  }
  return "Activity could not load. Try again.";
}

/** One bank line's account in words: name, then the last four only. */
function accountWords(item: ActivityItem): string {
  const account = item.account;
  if (account === undefined) return "";
  const name = amVisibleDisplayText(account.name).trim();
  const mask = amVisibleDisplayText(account.mask).trim();
  if (name === "" || mask === "") return name;
  return `${name} \u00b7 ${mask}`;
}

/** A labelled control inside the filter panel. */
function filterField(label: string, control: HTMLElement): HTMLElement {
  return h("label", { class: "activity-filter-field" }, [
    h("span", { class: "activity-filter-label" }, [label]),
    control,
  ]);
}

/**
 * "42", "42.5", "42.50" — what the amount window accepts. A magnitude, so no
 * sign: which way it reads is the Money in or out choice beside it (rd15 A4
 * fix round 1).
 */
function isAmountText(value: string): boolean {
  if (value === "") return true;
  return /^\d{1,13}(\.\d{1,2})?$/.test(value);
}

/**
 * The export's file name says which view it is: the range when there is one,
 * else the month. Nothing in it is user-typed prose.
 */
function activityCsvName(
  company: CompanySlug,
  from: string,
  to: string,
  period: string,
): string {
  // The slug is a file-name key, through the one key boundary, never words.
  const slug = slugText(company);
  if (from !== "" && to !== "") return `activity-${slug}-${from}-to-${to}.csv`;
  return `activity-${slug}-${period}.csv`;
}

/** Accounting-facing amount display (fixtures + live share this convention). */
export function formatAmount(amount: string): string {
  return formatDecimalMoney(amVisibleDisplayText(amount));
}

/**
 * Whether the Activity pager should enable Next.
 * Uses the server's hasMore only — never (page-1)*pageSize arithmetic.
 */
export function transactionsPageHasNext(page: { hasMore: boolean }): boolean {
  return page.hasMore;
}
